import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteForecastsFromAPI,
  fetchForecastsFromAPI,
  uploadForecastFileToAPI,
  uploadForecastJsonToAPI,
  downloadForecastFileFromAPI
} from "../../../services/ForecastService";

// Fetch Forecasts
export const fetchForecasts = createAsyncThunk(
  "forecasts/fetchForecasts",
  async ({ page, perPage, search, columnFilters, sort }, { rejectWithValue }) => {
    try {
      const response = await fetchForecastsFromAPI(page, perPage, search, columnFilters, sort);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch forecast data.");
    }
  }
);

// Delete Forecast
export const deleteForecast = createAsyncThunk(
  "forecasts/deleteForecast",
  async (forecastId, { rejectWithValue }) => {
    try {
      const response = await deleteForecastsFromAPI(forecastId);
      return forecastId; // Return ID so we can remove it from the state
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete forecast.");
    }
  }
);
// forecastsSlice.js or forecastsThunks.js
export const uploadForecastFile = createAsyncThunk(
  "forecasts/uploadForecastFile",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await uploadForecastFileToAPI(formData); // call API (axios/fetch)
      return response;
    } catch (error) {

      return rejectWithValue({
        message: error?.message || "Upload gagal.",
        detail: error?.detail || [],
        status: error?.status || "failed"
      });
    }
  }
);

// Upload Forecast JSON
export const uploadForecastJson = createAsyncThunk(
  "forecasts/uploadForecastJson",
  async ({ forecastData, forecastMonth, commodity, plant, fileName }, { rejectWithValue }) => {
    try {
      const response = await uploadForecastJsonToAPI({ forecastData, forecastMonth, commodity, plant, fileName });
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error?.message || "Upload JSON gagal.",
        detail: error?.detail || [],
        status: error?.status || "failed"
      });
    }
  }
);


// Download Forecast File
export const downloadForecastFile = createAsyncThunk(
  "forecasts/downloadForecastFile",
  async ({ fileType, vendorCode, period, commodity, plant }, { rejectWithValue }) => {
    try {
      const response = await downloadForecastFileFromAPI(fileType, vendorCode, period, commodity, plant);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to download forecast file.");
    }
  }
);

const forecastSlice = createSlice({
  name: "forecasts",
  initialState: {
    forecasts: [],
    columnFilters: {}, // per-column funnel filters, applied server-side
    sort: { sortBy: null, sortDir: 'asc' }, // server-side sort
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    firstItem: 1,
    forecastSearch: "",
    loading: false,
    error: null,
    totalCount: 0,
    keywordSearch: "",
  },
  reducers: {
    setSearchForecast: (state, action) => {
      state.forecastSearch = action.payload;
    },
    setColumnFilters: (state, action) => {
      state.columnFilters = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setVisibilityFilter: (state, action) => {
      state.currentFilter = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setKeywordSearch: (state, action) => {
      state.keywordSearch = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Forecasts
      .addCase(fetchForecasts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchForecasts.fulfilled, (state, action) => {
        console.log('✅ fetchForecasts.fulfilled - received data:', action.payload);
        const response = action.payload;
        const forecasts = response.data?.forecast || response.forecast || response;
        state.loading = false;
        state.forecasts = forecasts.data || forecasts;
        state.currentPage = forecasts.current_page || 1;
        state.lastPage = forecasts.last_page || 1;
        state.perPage = forecasts.per_page || 10;
        state.firstItem = forecasts.from || 1;
        state.totalCount = forecasts.total || (forecasts.data ? forecasts.data.length : forecasts.length);
        console.log('✅ fetchForecasts.fulfilled - final state:', {
          forecastsCount: state.forecasts.length,
          loading: state.loading,
          totalCount: state.totalCount,
          currentPage: state.currentPage,
          lastPage: state.lastPage,
          perPage: state.perPage,
          firstItem: state.firstItem
        });
      })
      .addCase(fetchForecasts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Forecast
      .addCase(deleteForecast.fulfilled, (state, action) => {
        state.forecasts = state.forecasts.filter((forecast) => forecast.id !== action.payload);
      })
      .addCase(deleteForecast.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Upload Forecast File
      .addCase(uploadForecastFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadForecastFile.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Upload Forecast JSON
      .addCase(uploadForecastJson.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadForecastJson.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Download Forecast File
      .addCase(downloadForecastFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadForecastFile.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  setSearchForecast,
  setPage,
  setVisibilityFilter,
  setPerPage,
  setColumnFilters,
  setSort,
  setKeywordSearch,
} = forecastSlice.actions;
export default forecastSlice.reducer;
